import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { 
  MapPin, Star, Shield, Phone, ChevronRight, Search, 
  Clock, CheckCircle, Users, Award
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LocationPage() {
  const { serviceSlug, locationSlug } = useParams();
  const [pageData, setPageData] = useState(null);
  const [tradies, setTradies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPageData();
    fetchTradies();
  }, [serviceSlug, locationSlug]);

  const fetchPageData = async () => {
    try {
      const response = await axios.get(`${API}/seo/page-data/${serviceSlug}/${locationSlug}`);
      setPageData(response.data);
    } catch (error) {
      console.error("Error fetching page data:", error);
    }
  };

  const fetchTradies = async () => {
    setLoading(true);
    try {
      // Convert slug back to suburb name for search
      const suburbName = locationSlug.split('-').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
      
      const response = await axios.get(`${API}/tradies`, {
        params: {
          service: serviceSlug,
          suburb: suburbName,
          limit: 50
        }
      });
      setTradies(response.data);
    } catch (error) {
      console.error("Error fetching tradies:", error);
      toast.error("Failed to load tradies");
    } finally {
      setLoading(false);
    }
  };

  if (!pageData) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-24 pb-16 flex items-center justify-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#f97316] border-r-transparent"></div>
        </div>
        <Footer />
      </div>
    );
  }

  const { service, location, seo, schema, tradie_count } = pageData;

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta name="keywords" content={seo.keywords} />
        <link rel="canonical" href={seo.canonical} />
        
        {/* Open Graph */}
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={seo.canonical} />
        
        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(schema.main)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(schema.faq)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(schema.breadcrumb)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-slate-50">
        <Navbar />

        {/* Hero Section */}
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-24 pb-16">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
            {/* Breadcrumbs */}
            <nav className="flex items-center text-sm text-slate-400 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <Link to="/browse" className="hover:text-white transition-colors">Find Tradies</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <Link to={`/browse?service=${serviceSlug}`} className="hover:text-white transition-colors">
                {service.name}
              </Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span className="text-white">{location.suburb}</span>
            </nav>

            {/* Main Heading */}
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {service.name} {location.suburb}
            </h1>
            
            <p className="text-xl text-slate-300 mb-8 max-w-3xl">
              {seo.intro}
            </p>

            {/* Quick Stats */}
            <div className="flex flex-wrap gap-6 mb-8">
              <div className="flex items-center text-white">
                <Users className="w-5 h-5 mr-2 text-[#f97316]" />
                <span className="font-semibold">{tradie_count}+</span>
                <span className="text-slate-400 ml-1">Local {service.name}</span>
              </div>
              <div className="flex items-center text-white">
                <Shield className="w-5 h-5 mr-2 text-green-400" />
                <span>Licensed & Insured</span>
              </div>
              <div className="flex items-center text-white">
                <Clock className="w-5 h-5 mr-2 text-blue-400" />
                <span>24/7 Emergency Available</span>
              </div>
            </div>

            {/* CTA Button */}
            <Link to={`/get-quote?service=${serviceSlug}&suburb=${location.suburb}`}>
              <Button size="lg" className="bg-[#f97316] hover:bg-[#ea580c] text-white text-lg px-8 py-6">
                <Search className="w-5 h-5 mr-2" />
                Get Free Quotes from {location.suburb} {service.name}
              </Button>
            </Link>
          </div>
        </div>

        {/* Tradies List Section */}
        <div className="py-16">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {tradies.length > 0 ? `${tradies.length} ${service.name} ${location.suburb}` : `${service.name} ${location.suburb}`}
              </h2>
              <Badge variant="outline" className="text-slate-600">
                <MapPin className="w-3 h-3 mr-1" />
                {location.state} {location.postcode}
              </Badge>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#f97316] border-r-transparent"></div>
              </div>
            ) : tradies.length === 0 ? (
              <Card className="p-12 text-center">
                <CardContent>
                  <p className="text-lg text-slate-600 mb-4">
                    No {service.name.toLowerCase()} listed in {location.suburb} yet.
                  </p>
                  <p className="text-slate-500 mb-6">
                    But don't worry! Submit a quote request and we'll connect you with qualified {service.name.toLowerCase()} in the {location.region || location.state} area.
                  </p>
                  <Link to={`/get-quote?service=${serviceSlug}&suburb=${location.suburb}`}>
                    <Button className="bg-[#f97316] hover:bg-[#ea580c]">
                      Submit a Quote Request
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {tradies.map((tradie) => (
                  <Link
                    key={tradie.id}
                    to={`/tradie/${tradie.id}`}
                    className="group relative overflow-hidden rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="p-6">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="font-bold text-xl text-[#0f172a] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {tradie.business_name}
                          </h3>
                          <p className="text-sm text-slate-600">{tradie.contact_name}</p>
                        </div>
                        {tradie.verified && (
                          <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                            <Shield className="w-3 h-3 mr-1" />
                            Verified
                          </Badge>
                        )}
                      </div>

                      {/* Rating */}
                      {tradie.rating > 0 && (
                        <div className="flex items-center space-x-2 mb-4">
                          <div className="flex items-center">
                            <Star className="w-4 h-4 fill-orange-400 text-orange-400" />
                            <span className="ml-1 font-semibold text-[#0f172a]">{tradie.rating.toFixed(1)}</span>
                          </div>
                          <span className="text-sm text-slate-500">rating</span>
                        </div>
                      )}

                      {/* Location */}
                      <div className="flex items-center text-sm text-slate-600 mb-4">
                        <MapPin className="w-4 h-4 mr-2" />
                        <span>{tradie.suburb}, {tradie.state} {tradie.postcode}</span>
                      </div>

                      {/* Description */}
                      <p className="text-sm text-slate-600 line-clamp-2 mb-4">
                        {tradie.description}
                      </p>

                      {/* CTA */}
                      <Button className="w-full bg-[#f97316] hover:bg-[#ea580c] text-white font-bold">
                        View Profile & Contact
                      </Button>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* FAQ Section - Good for SEO */}
        <div className="bg-white py-16">
          <div className="container mx-auto px-4 md:px-6 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Frequently Asked Questions - {service.name} {location.suburb}
            </h2>
            
            <div className="space-y-6">
              <div className="bg-slate-50 rounded-lg p-6">
                <h3 className="font-semibold text-lg text-slate-900 mb-2">
                  How much do {service.name.toLowerCase()} charge {location.suburb}?
                </h3>
                <p className="text-slate-600">
                  Prices for {service.name.toLowerCase()} {location.suburb} vary based on the job scope. 
                  Get free quotes from multiple verified professionals to compare prices and find the best value for your project.
                </p>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-6">
                <h3 className="font-semibold text-lg text-slate-900 mb-2">
                  How do I find a reliable {service.name.toLowerCase().replace(/s$/, '')} {location.suburb}?
                </h3>
                <p className="text-slate-600">
                  Use Local Tradies Online to browse verified {service.name.toLowerCase()} {location.suburb}. 
                  All listed tradies are licensed and insured for your protection. Look for the verified badge and read customer reviews.
                </p>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-6">
                <h3 className="font-semibold text-lg text-slate-900 mb-2">
                  Do {service.name.toLowerCase()} {location.suburb} offer free quotes?
                </h3>
                <p className="text-slate-600">
                  Yes, most {service.name.toLowerCase()} on our platform offer free quotes. Simply submit your job details 
                  and receive competitive quotes from local professionals {location.suburb} and the surrounding {location.region || location.state_full} area.
                </p>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-6">
                <h3 className="font-semibold text-lg text-slate-900 mb-2">
                  Are {service.name.toLowerCase()} {location.suburb} available for emergencies?
                </h3>
                <p className="text-slate-600">
                  Many {service.name.toLowerCase()} {location.suburb} offer 24/7 emergency services. 
                  Look for the "24/7 Available" badge on tradie profiles, or submit an urgent quote request for fast response.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Related Locations */}
        <div className="py-16">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
            <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {service.name} Nearby Areas
            </h2>
            <div className="flex flex-wrap gap-3">
              {/* This would be dynamically populated with nearby locations */}
              <Link to={`/browse?service=${serviceSlug}`} className="text-[#f97316] hover:text-[#ea580c] font-medium">
                View all {service.name} →
              </Link>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}
